import React, {
  useMemo,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import _ from "lodash";
import * as yup from "yup";
import { Formik, Form } from "formik";
import { Flex, Box } from "reflexbox";
import { useTheme } from "styled-components";
import Card from "../../UI/Card";
// import ContactForm from "./ContactForm";
import Field from "../../UI/forms/Field";
import { useQuery } from "../../Hooks";
import * as QMR from "../../../requests/quoteManagementRequests";
import * as CMR from "../../../requests/customerManagementRequests";
import AlertContext from "../../../context/alert/AlertContext";
import ConfiguratorContext from "../../../context/configurator/ConfiguratorContext";
import { deleteAddress } from "../../../requests/addressManagementRequests";
import UserContext from "../../../context/user/UserContext";
import { useParams } from "react-router-dom";
import { Body, Helper } from "../../UI/Text";
import AddressHeader from "./AddressHeader";
import AddressForm from "./AddressForm";

const { getCustomers } = CMR;
const { updateQuote } = QMR;

const validationSchema = yup.object().shape({
  customerId: yup.string().nullable(),
  shippingAddressId: yup.string().nullable(),
  sameAsBilling: yup.boolean().nullable(),
  contact: yup.object().shape({
    firstName: yup.string().nullable(),
    lastName: yup.string().nullable(),
    email: yup.string().email().nullable(),
    phone: yup.string().nullable(),
  }),
  customer: yup.object().shape({
    companyName: yup.string().nullable(),
  }),

  billing: yup.object().shape({
    Address: yup.string().nullable(),
    Address2: yup.string().nullable(),
    City: yup.string().required("City is required."),
    County: yup.string().nullable(),
    State: yup.string().required("State is required."),
    Country: yup.string().required("Country is required."),
    Zip: yup.string().required("Zip Code is required."),
  }),

  shipCode: yup.string().required(),
  shipping: yup.object().shape({
    Address: yup.string().nullable(),
    Address2: yup.string().nullable(),
    City: yup.string().required("City is required."),
    County: yup.string().nullable(),
    State: yup.string().required("State is required."),
    Country: yup.string().required("Country is required."),
    Zip: yup.string().required("Zip Code is required."),
  }),

  address: yup.object().shape({
    Address: yup.string().nullable(),
    Address2: yup.string().nullable(),
    City: yup.string().nullable(),
    State: yup.string().nullable(),
    Zip: yup.string().nullable(),
  }),
});

const Customer = ({ isLoading, refetchQuote, refetchCustomer }) => {
  const theme = useTheme();
  const { quoteId } = useParams();
  const { quote, setQuote } = useContext(ConfiguratorContext);
  const { activeUser } = useContext(UserContext);
  const roleLevel = useMemo(() => activeUser?.roleLevel || 0, [activeUser]);
  const { showSuccessAlert, showFailureAlert } = useContext(AlertContext);

  const { pid, groupIds } = activeUser || {};

  const [shouldRefetch, setRefetch] = useState(null);

  const shippingAddresses = useMemo(
    () =>
      roleLevel <= 1
        ? activeUser?.shippingAddresses
        : quote?.shippingAddresses || [],
    [activeUser, quote, roleLevel]
  );

  const throttle = useRef(null);

  useEffect(() => {
    switch (shouldRefetch) {
      case 0:
        // throttle.current = timeout;
        break;
      case 1:
        if (!throttle.current) {
          const timeout = setTimeout(() => {
            clearTimeout(throttle);
            setRefetch(2);
          }, 5000);
          throttle.current = timeout;
        }
        break;
      case 2:
        refetchQuote();
        clearTimeout(throttle.current);
        setRefetch(null);
        break;
      default:
        throttle.current = null;
        break;
    }
  }, [refetchQuote, shouldRefetch]);

  const { data, isLoading: customersLoading } = useQuery({
    request: getCustomers,
    skip: !pid || roleLevel <= 1,
    queryVariables: { pid, hasERP: true, groupIds, groupId: quote.groupId },
  });
  const { rows: customers = [] } = data || {};

  const customerOptions = useMemo(() => {
    return !_.isArray(customers)
      ? []
      : customers.map((row) => {
          const { billingAddress } = quote || {};
          const { customer } = row || {};
          const { id, erpId, companyName } = customer || {};

          const { Address, Address2, City, State, Zip } = billingAddress || {};

          let label = `${companyName || erpId || "—"}`;

          if (Address || Address2) {
            if (Address) label += `, ${Address}`;
            if (Address2) {
              label += ` ${Address2}`;
            }
            if (City) {
              label += `, ${City}`;
              if (State) {
                label += ` ${State}`;
                if (Zip) {
                  label += `, ${Zip}`;
                }
              }
            }
          }

          return { label, value: id };
        });
  }, [customers, quote]);

  const onOptionClick = useCallback(
    async (id, resetForm) => {
      if (!id) {
        const customer = await CMR.createCustomer({
          input: {
            groupId: activeUser?.groupId,
            erpId: "",
            pid: activeUser?.pid,
          },
        });
        const { id: cid } = customer;
        await updateQuote({
          id: +quoteId || +quote?.id,
          input: { contactId: cid },
        });

        refetchCustomer();
        setQuote({
          ...quote,
          customer: null,
          billingAddress: null,
          shippingAddress: null,
        });
      } else {
        await updateQuote({
          id: +quoteId || +quote?.id,
          input: { contactId: id },
        });
      }
      setQuote(null);
      refetchQuote();
      if (resetForm) resetForm();
    },
    [activeUser, quote, quoteId, refetchCustomer, refetchQuote, setQuote]
  );
  const [editingId, setEditingId] = useState(null);

  const addressValues = useMemo(() => {
    return (
      _.find(
        [
          ...(quote?.shippingAddresses || []),
          quote?.billingAddress,
          quote?.shippingAddress,
        ],
        (address) => address?.id === editingId
      ) || {}
    );
  }, [editingId, quote]);

  const initialValues = useMemo(() => {
    const { firstName, lastName, email, phone, altPhone } =
      roleLevel <= 1 ? activeUser?.contact : quote?.contact || {};
    const { companyName } = quote?.customer || {};

    return {
      contact: {
        firstName: firstName || "",
        lastName: lastName || "",
        email: email || "",
        phone: phone || "",
        altPhone: altPhone || "",
      },
      customer: {
        companyName: companyName || "",
      },
      sameAsBilling:
        roleLevel <= 1 ? undefined : !!quote?.sameAsBilling ? true : false,
      shippingAddressId:
        roleLevel <= 1
          ? activeUser?.customer?.shippingAddressId
          : quote?.shippingAddressId || "",
      customerId:
        roleLevel <= 1 ? activeUser?.customer?.id : quote?.contactId || "",
      billing:
        roleLevel <= 1 ? activeUser?.billingAddress : quote?.billingAddress,
      shipping:
        roleLevel <= 1 ? activeUser?.shippingAddress : quote?.shippingAddress,
      address: {
        ...addressValues,
      },
    };
  }, [quote, activeUser, roleLevel, addressValues]);

  const ref = useRef(null);
  const [customerTypedId, setCustomerTypedId] = useState("");

  const placeholder = useMemo(() => {
    if (!!customerTypedId) {
      const { 0: option } =
        _.filter(customerOptions, (op) =>
          op?.label?.startsWith(customerTypedId)
        ) || {};

      return option?.label;
    }
    const op = quote?.contactId
      ? _.find(customerOptions, ({ value }) => +value === +quote?.contactId)
      : _.find(customerOptions, ({ value }) => +value === +quote?.customer?.id);
    return op?.label || "Select a Customer";
  }, [customerOptions, customerTypedId, quote]);

  const onCustomerChange = useCallback(
    (e) => {
      const label = e.target.value;
      const { value } =
        _.find(customerOptions, ({ label: _label }) => _label === label) || {};

      setCustomerTypedId(value);

      if (ref.current) clearTimeout(ref.current);

      ref.current = setTimeout(async () => {
        await onOptionClick(value);
        setCustomerTypedId("");
      }, 500);
    },
    [customerOptions, onOptionClick]
  );

  const [formToShow, setFormToShow] = useState("");

  const [shippingColor, setShippingColor] = useState(null);

  const onSetShippingAddress = useCallback(
    async (address) => {
      setShippingColor(theme.secondary);
      setQuote({
        ...quote,
        shippingAddressId: address?.id,
        shippingAddress: address,
      });
      const { error, success } = await updateQuote({
        id: quoteId,
        input: { shippingAddressId: address?.id },
      });
      setShippingColor(null);

      if (success === true) {
        showSuccessAlert("Successfully updated shipping address.");
      } else {
        showFailureAlert(error);
      }
    },
    [
      quote,
      quoteId,
      setQuote,
      showFailureAlert,
      showSuccessAlert,
      theme.secondary,
    ]
  );

  const onDeleteShippingAddress = useCallback(
    async (id) => {
      try {
        await deleteAddress({ id });
        const adds = [...(quote?.shippingAddresses || [])];
        _.remove(adds, ({ id: sID }) => sID === id);
        setQuote({ ...quote, shippingAddresses: adds });

        return showSuccessAlert(`Address deleted successfully.`);
      } catch (error) {
        return showFailureAlert(error);
      }
    },
    [quote, setQuote, showFailureAlert, showSuccessAlert]
  );

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
        validateOnChange={false}
      >
        <Box mt="1rem">
          <Card>
            <Form style={{ width: "100%" }}>
              {!!formToShow ? (
                <AddressForm
                  {...{
                    formToShow,
                    setFormToShow,
                    setEditingId,
                    editingId,
                  }}
                />
              ) : (
                <>
                  {/* {activeUser?.roleLevel <= 1 ||
                  activeUser?.groupId === null ? null : (
                    <Box width={1} flex={1}>
                      <Field
                        ref={ref}
                        typeableSelect
                        onChange={onCustomerChange}
                        options={[
                          <option key="New Customer" value={null}>
                            New Customer
                          </option>,
                          ...customerOptions.map(({ label, value }) => (
                            <option id={value} key={value} value={label}>
                              {value}
                            </option>
                          )),
                        ]}
                        name="customerId"
                        labelText="Company"
                        placeholder={placeholder}
                        value={customerTypedId}
                        isLoading={customersLoading}
                      />
                    </Box>
                  )} */}

                  <AddressHeader
                    setEditingId={setEditingId}
                    setFormToShow={setFormToShow}
                    shippingColor={shippingColor}                    
                    hideCustomerEdit={true}
                  />

                  <Flex
                    width={1}
                    flexDirection={["column", "row", "row"]}
                    style={{ gap: "1rem", flexFlow: "wrap" }}
                    flexWrap={["unset", "unset", "wrap", "wrap"]}
                  >
                    {/* <Card
                      interactive
                      width={[
                        "100%",
                        "100%",
                        "100%",
                        `calc(50% - 0.5rem)`,
                        `calc(25% - 0.25rem)`,
                        `calc(20% - 0.2rem)`,
                      ]}
                      onClick={() => {
                        setEditingId(null);
                        setFormToShow("address");
                      }}
                      iconName="faPlus"
                    >
                      <Body bold center>
                        Add Shipping Address
                      </Body>
                      <Helper center>
                        Create a new shipping address that will be assigned to
                        this customer for future use.
                      </Helper>
                    </Card> */}

                    {/* {shippingAddresses?.map((address) => {
                      const { Address, Address2, City, State, Zip, id } =
                        address;
                      return !Address && !City && !State && !Zip ? null : (
                        <Card
                          interactive
                          width={[
                            "100%",
                            "100%",
                            "100%",
                            `calc(50% - 0.5rem)`,
                            `calc(25% - 0.25rem)`,
                            `calc(20% - 0.2rem)`,
                          ]}
                          onClick={() => {
                            onSetShippingAddress(address);
                          }}
                          key={id}
                          onEdit={() => {
                            setEditingId(id);
                            setFormToShow("address");
                          }}
                          onDelete={() => onDeleteShippingAddress(id)}
                        >
                          <Body>
                            {Address || Address2
                              ? `${Address || ""}${
                                  Address2 && Address ? " " : ""
                                }${Address2 || " "}`
                              : "—"}
                          </Body>
                          <Body>{`${City}, ${State} ${Zip}`}</Body>
                        </Card>
                      );
                    })} */}
                    {/* <Box flex={1}>
                      <ContactForm
                      disableForm={
                        (isLoading || quote?.customer?.isDealer) &&
                        values["customerId"] !== ""
                      }
                      onSubmit={onSubmit}
                      isLoading={isLoading}
                      setRefetch={setRefetch}
                      resetForm={resetForm}
                      />
                      </Box>
                      
                     */}
                  </Flex>
                </>
              )}
            </Form>
          </Card>
        </Box>
      </Formik>
    </>
  );
};

export default Customer;
